import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/calendar-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch users');
      }

      return data.users;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const saveColumnOrder = createAsyncThunk(
  'users/saveColumnOrder',
  async ({ type, columnOrder }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/column-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, columnOrder })
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to save column order');
      }

      return { type, columnOrder: data.columnOrder };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchColumnOrder = createAsyncThunk(
  'users/fetchColumnOrder',
  async (type, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/column-order/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch column order');
      }

      return { type, columnOrder: data.columnOrder };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const updateMenuColor = createAsyncThunk(
  'users/updateMenuColor',
  async ({ menuColor, customMenuColor }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/menu-color`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ menuColor, customMenuColor })
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update menu color');
      }

      return { 
        menuColor: data.menuColor, 
        customMenuColor: data.customMenuColor 
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchMenuColor = createAsyncThunk(
  'users/fetchMenuColor',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/menu-color`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch menu color');
      }

      return { 
        menuColor: data.menuColor,
        customMenuColor: data.customMenuColor,
        greyMenuColor: data.greyMenuColor,
        customGreyMenuColor: data.customGreyMenuColor
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const updateGreyMenuColor = createAsyncThunk(
  'users/updateGreyMenuColor',
  async ({ greyMenuColor, customGreyMenuColor }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/grey-menu-color`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ greyMenuColor, customGreyMenuColor })
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update grey menu color');
      }

      return {
        greyMenuColor: data.greyMenuColor,
        customGreyMenuColor: data.customGreyMenuColor
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchGreyMenuColor = createAsyncThunk(
  'users/fetchGreyMenuColor',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/grey-menu-color`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch grey menu color');
      }

      return { 
        greyMenuColor: data.greyMenuColor,
        customGreyMenuColor: data.customGreyMenuColor,
        greyMenuDisplayMode: data.greyMenuDisplayMode
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const updateGreyMenuDisplayMode = createAsyncThunk(
  'users/updateGreyMenuDisplayMode',
  async ({ greyMenuDisplayMode }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/grey-menu-display-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ greyMenuDisplayMode })
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update grey menu display mode');
      }

      return { greyMenuDisplayMode: data.greyMenuDisplayMode };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Read persisted menu layout preference
const getStoredMenuLayout = () => {
  try {
    const v = localStorage.getItem('topsail:menuLayout');
    return v === 'classic' || v === 'modern' ? v : 'modern';
  } catch (_) {
    return 'modern';
  }
};

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    loading: false,
    error: null,
    columnOrders: {
      leads: [],
      projects: [],
      clients: []
    },
    menuColor: 'blue',
    customMenuColor: null,
    greyMenuColor: 'custom',
    customGreyMenuColor: '#f9fafb',
    greyMenuDisplayMode: 'text',
    menuLayout: getStoredMenuLayout()
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setMenuLayout: (state, action) => {
      const next = action.payload === 'classic' ? 'classic' : 'modern';
      state.menuLayout = next;
      try { localStorage.setItem('topsail:menuLayout', next); } catch (_) {}
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(saveColumnOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveColumnOrder.fulfilled, (state, action) => {
        state.loading = false;
        const { type, columnOrder } = action.payload;
        state.columnOrders[type] = columnOrder;
      })
      .addCase(saveColumnOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchColumnOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColumnOrder.fulfilled, (state, action) => {
        state.loading = false;
        const { type, columnOrder } = action.payload;
        state.columnOrders[type] = columnOrder;
      })
      .addCase(fetchColumnOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateMenuColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMenuColor.fulfilled, (state, action) => {
        state.loading = false;
        state.menuColor = action.payload.menuColor;
        state.customMenuColor = action.payload.customMenuColor;
      })
      .addCase(updateMenuColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchMenuColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuColor.fulfilled, (state, action) => {
        state.loading = false;
        state.menuColor = action.payload.menuColor;
        state.customMenuColor = action.payload.customMenuColor;
        state.greyMenuColor = action.payload.greyMenuColor;
        state.customGreyMenuColor = action.payload.customGreyMenuColor;
      })
      .addCase(fetchMenuColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateGreyMenuColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGreyMenuColor.fulfilled, (state, action) => {
        state.loading = false;
        state.greyMenuColor = action.payload.greyMenuColor;
        state.customGreyMenuColor = action.payload.customGreyMenuColor;
      })
      .addCase(updateGreyMenuColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchGreyMenuColor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGreyMenuColor.fulfilled, (state, action) => {
        state.loading = false;
        state.greyMenuColor = action.payload.greyMenuColor;
        state.customGreyMenuColor = action.payload.customGreyMenuColor;
        state.greyMenuDisplayMode = action.payload.greyMenuDisplayMode;
      })
      .addCase(fetchGreyMenuColor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateGreyMenuDisplayMode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGreyMenuDisplayMode.fulfilled, (state, action) => {
        state.loading = false;
        state.greyMenuDisplayMode = action.payload.greyMenuDisplayMode;
      })
      .addCase(updateGreyMenuDisplayMode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      });
  }
});

export const { clearError, setMenuLayout } = usersSlice.actions;
export default usersSlice.reducer;
